import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { usePostCode } from './usePostCode';

/**
 * Mirrors the currently-selected postcode to a level *above*
 * PlacesAutocompleteProvider.
 *
 * `usePostCode` derives the postcode from PlacesAutocomplete and so is only
 * callable inside that provider. But JobProvider/ReviewProvider sit above it
 * (see entrypoint/Widget.tsx), and their v2 by-id fetchers (EN-1328) need the
 * selected area to page `serviceArea/content` — v2 has no global by-id lookup
 * the way legacy `featured/job` did. `SelectedPostCodeSync`, mounted inside the
 * autocomplete scope, feeds the value up into this context; the fetchers read it
 * via `useSelectedPostCode`. Undefined while nothing is selected — the fetchers
 * then fall back to the v1 by-id endpoint.
 */

type SelectedPostCodeContextType = {
    selectedPostCode: string | undefined;
    setSelectedPostCode: (postCode: string | undefined) => void;
};

const SelectedPostCodeContext = createContext<
    SelectedPostCodeContextType | undefined
>(undefined);

export const SelectedPostCodeProvider = ({
    children,
}: PropsWithChildren<unknown>): JSX.Element => {
    const [selectedPostCode, setSelectedPostCode] = useState<
        string | undefined
    >();

    const value = useMemo(
        () => ({ selectedPostCode, setSelectedPostCode }),
        [selectedPostCode]
    );

    return (
        <SelectedPostCodeContext.Provider value={value}>
            {children}
        </SelectedPostCodeContext.Provider>
    );
};

/**
 * Bridges `usePostCode` (valid only inside PlacesAutocompleteProvider) into the
 * context above. Renders nothing; mount it within the autocomplete scope. A
 * no-op when no SelectedPostCodeProvider is present.
 */
export const SelectedPostCodeSync = (): null => {
    const postCode = usePostCode();
    const context = useContext(SelectedPostCodeContext);
    const setSelectedPostCode = context?.setSelectedPostCode;

    useEffect(() => {
        setSelectedPostCode?.(postCode);
    }, [postCode, setSelectedPostCode]);

    return null;
};

/**
 * The selected postcode, readable above PlacesAutocompleteProvider. Undefined
 * until `SelectedPostCodeSync` reports one (or when used with no provider).
 */
export const useSelectedPostCode = (): string | undefined => {
    return useContext(SelectedPostCodeContext)?.selectedPostCode;
};
